import { useEffect, useRef, useState, useCallback } from 'react'
import * as Icons from 'lucide-react'
import { Sliders, Zap, ZapOff, AlertCircle, Grid3x3, RotateCw } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  listInputs, listOutputs, attachInputListener,
  sendLED, clearAllLeds,
} from '@/lib/tournamentsMidi'
import { presetKleur, schermKleur } from '@/lib/screenColors'

/* MIDI-bediening voor de schermregie — APC Mini mk2 als regietafel.

     · 8×8 pads      raster van scherm × inhoud
                     · rij = het scherm dat op die APC-rij zit
                     · kolom = de inhoud voor dat scherm-type op die kolom
                     Deelnemer-schermen krijgen de deelnemer-content-set,
                     admin-schermen krijgen de admin-set.
     · scene-knoppen rechts   opgeslagen scenes (hele zaal in één klap)
     · track-knoppen onder    auto-switch aan/uit per scherm
     · faders                 rotatie-interval per scherm (onderaan traag, boven snel)

   De LPD8 is bewust NIET aan de schermregie gekoppeld — die reserveren we
   voor de bediening van Kempenvision en Image Compo. */

const OPSLAG = {
  actief: 'screens-midi-actief',
  apc:    'screens-midi-apc-in',
  apcOut: 'screens-midi-apc-out',
}
const lees    = (k, d = '') => { try { return localStorage.getItem(k) ?? d } catch { return d } }
const schrijf = (k, v) => { try { v ? localStorage.setItem(k, v) : localStorage.removeItem(k) } catch {} }

/* ── Notennummers op de APC Mini mk2 ──────────────────────────────
   Pads: rij 0 is onderaan (0-7), rij 7 bovenaan (56-63).
   Scene-knoppen rechts (verticale rij): 112-119 (boven naar beneden).
   Track-knoppen onder (horizontale rij onder de pads): 100-107.
   Faders: CC 48-56, waarvan 48-55 de kanaalfaders zijn.                */
const apcNoot   = (rijVanBoven, kolom) => 56 - (rijVanBoven * 8) + kolom
const SCENE_EERSTE = 112
const TRACK_EERSTE = 100
const FADER_EERSTE = 48

/** Zet een faderwaarde (0-127) om naar een bruikbaar interval in seconden. */
const faderNaarSeconden = (v) => Math.round(5 + (v / 127) * 175)   // 5 s … 3 min

function raadRol(naam = '') {
  const n = naam.toLowerCase()
  return n.includes('apc') ? 'apc' : null
}

function icoonVan(naam) { return Icons[naam] || Icons.MonitorPlay }

/* Scene- en track-knoppen op de mk2 zijn geen RGB — ze accepteren een simpel
   velocity-schema (LiveController-standaard):
     0 off · 1 groen · 2 groen blink · 3 rood · 4 rood blink · 5 amber · 6 amber blink
   We nemen alleen de 3 hoofdkleuren + hun blink-variant. */
const KNOP_VEL = {
  off:          0,
  green:        1, green_blink: 2,
  red:          3, red_blink:   4,
  amber:        5, amber_blink: 6,
}
/** Val terug op amber voor RGB-namen die geen equivalent hebben op deze knop. */
function knopKleurCategorie(k) {
  if (!k || k === 'off') return 'off'
  if (['red','pink','magenta'].includes(k))  return 'red'
  if (['green','lime'].includes(k))          return 'green'
  return 'amber'   // yellow, orange, white, blue, cyan, purple, …
}
function stuurKnopLed(out, noot, kleur = 'off') {
  if (!out) return
  // Als de meegegeven waarde direct in de vel-tabel zit ('green_blink' etc.),
  // gebruik die letterlijk. Anders eerst mappen via kleur-categorie.
  const direct = KNOP_VEL[kleur]
  const vel = direct !== undefined ? direct : (KNOP_VEL[knopKleurCategorie(kleur)] ?? 0)
  try { out.send([0x90, noot & 0x7F, vel & 0x7F]) } catch {}
}

export function useScreenMidi({
  screens, presets, scenes = [],
  onSetRoute, onApplyScene, onToggleRotation, onSetInterval,
}) {
  const [actief, setActief]   = useState(() => lees(OPSLAG.actief) === '1')
  const [status, setStatus]   = useState({ apc: 'uit' })
  const [laatste, setLaatste] = useState(null)
  const [inputs, setInputs]   = useState([])
  const [outputs, setOutputs] = useState([])
  const [keuze, setKeuze]     = useState(() => ({
    apc: lees(OPSLAG.apc), apcOut: lees(OPSLAG.apcOut),
  }))

  const ref = useRef({ screens, presets, scenes, onSetRoute, onApplyScene, onToggleRotation, onSetInterval })
  useEffect(() => {
    ref.current = { screens, presets, scenes, onSetRoute, onApplyScene, onToggleRotation, onSetInterval }
  })
  const apcOutRef = useRef(null)
  const faderTimers = useRef({})

  const zet = (v) => { setActief(v); schrijf(OPSLAG.actief, v ? '1' : '') }
  const kies = (rol, id) => {
    setKeuze(k => ({ ...k, [rol]: id }))
    schrijf({ apc: OPSLAG.apc, apcOut: OPSLAG.apcOut }[rol], id)
  }

  const meld = useCallback((tekst) => setLaatste(tekst), [])

  /** Voor elke APC-noot: welke SCHERMEN + welke inhoud er hoort. 2D-matrix:
      meerdere schermen kunnen op dezelfde rij zitten, en een preset moet zelf
      op die rij zijn ingedeeld om beschikbaar te zijn. `screens` is een array
      omdat één knop meerdere schermen tegelijk kan omschakelen. */
  const apcIndeling = useCallback(() => {
    const m = new Map()
    const { screens: sc, presets: pr } = ref.current
    // Legacy fallback: als midi_rows leeg is maar midi_row wel gezet → gebruik die.
    const rijenVan = (x) => (x?.midi_rows?.length
      ? x.midi_rows
      : (x?.midi_row != null ? [x.midi_row] : []))
    for (let rij = 0; rij < 8; rij++) {
      const schermenOpRij = sc.filter(s => rijenVan(s).includes(rij))
      if (schermenOpRij.length === 0) continue
      // Presets die op deze rij zichtbaar mogen zijn — kind-filter volgt het
      // eerste scherm op de rij (verwachting: alle schermen op één rij delen
      // hetzelfde kind; anders wint het eerste).
      const kindRij = schermenOpRij[0]?.kind || 'participant'
      const eigenPresets = pr.filter(p => p.kind === kindRij
                                       && rijenVan(p).includes(rij))
      for (let kol = 0; kol < 8; kol++) {
        const preset = eigenPresets.find(p => p.midi_col === kol)
        if (!preset) continue
        m.set(apcNoot(rij, kol), { schermen: schermenOpRij, preset })
      }
    }
    return m
  }, [])

  const voerPadUit = useCallback((actie, bron) => {
    if (!actie) return
    const r = ref.current
    const namen = actie.schermen.map(s => s.name).join(', ')
    meld(`${bron} → ${namen}: ${actie.preset.label}`)
    for (const sc of actie.schermen) r.onSetRoute?.(sc.id, actie.preset.route)
  }, [meld])

  /** Elke pad krijgt precies één status: off / solid preset-kleur / blink.
      Blink zetten we altijd na een solid-command — APC mk2 firmware start
      de blink-modus alleen betrouwbaar vanaf een aan-toestand. */
  const tekenLeds = useCallback(() => {
    const out = apcOutRef.current
    if (!out) return
    const r = ref.current
    const kaart = apcIndeling()

    for (let n = 0; n <= 63; n++) {
      const actie = kaart.get(n)
      if (!actie) {
        try { sendLED(out, `note:0:${n}`, 'off', 'mk2') } catch {}
        continue
      }
      const basis = actie.preset?.led_color || 'white'
      // 2D: als ALLE schermen op deze rij zwart zijn → blink. Anders alleen
      // knop live als ALLE schermen deze route tonen (consistente state).
      const routes = actie.schermen.map(s => s.current_route)
      const isZwart  = routes.every(cr => cr == null)
      const isActief = routes.every(cr => cr === actie.preset.route) && !isZwart
      const moetBlinken = isActief
      try {
        // MK2-firmware moet expliciet uit een blink-modus getrokken worden —
        // een nieuwe solid-velocity alleen is niet altijd genoeg. Eerst OFF,
        // daarna solid, en enkel bij actie ook de blink erover heen.
        sendLED(out, `note:0:${n}`, 'off', 'mk2')
        sendLED(out, `note:0:${n}`, basis, 'mk2')
        if (moetBlinken) sendLED(out, `note:0:${n}`, `${basis}_blink`, 'mk2')
      } catch {}
    }
    // Scene-knoppen rechts: op mk2 is de scene-LED hardwired groen, dus
    // effectief kunnen we alleen kiezen tussen solid en blink per scene.
    for (let i = 0; i < 8; i++) {
      const scene = r.scenes.find(sc => sc.midi_slot === i + 1)
      if (!scene) { stuurKnopLed(out, SCENE_EERSTE + i, 'off'); continue }
      stuurKnopLed(out, SCENE_EERSTE + i, scene.blink ? 'green_blink' : 'green')
    }
    // Track-knoppen onder: één per rij — groen als rotatie loopt, uit als niet.
    for (let i = 0; i < 8; i++) {
      const sc = r.screens.find(s => (s.midi_rows?.length ? s.midi_rows.includes(i) : s.midi_row === i))
      stuurKnopLed(out, TRACK_EERSTE + i, sc?.rotation_active ? 'green' : 'off')
    }
  }, [apcIndeling])

  useEffect(() => {
    if (!actief) { setStatus({ apc: 'uit' }); return }
    let losmaken = [], gestopt = false

    ;(async () => {
      let access
      try { access = await navigator.requestMIDIAccess?.({ sysex: false }) }
      catch (e) { console.warn('MIDI-toegang geweigerd', e) }
      if (!access || gestopt) { setStatus({ apc: 'fout' }); return }

      const ins  = listInputs(access);  setInputs(ins)
      const outs = listOutputs(access); setOutputs(outs)

      const apcIn  = ins.find(i => i.id === keuze.apc)     ?? ins.find(i => raadRol(i.name) === 'apc')
      const apcOut = outs.find(o => o.id === keuze.apcOut) ?? outs.find(o => raadRol(o.name) === 'apc')
      const nieuw = { apc: 'niet gevonden' }

      if (apcIn) {
        losmaken.push(attachInputListener(apcIn, (evt) => {
          const r = ref.current
          if (evt.type === 0x90) {
            const noot = evt.data1
            if (noot >= SCENE_EERSTE && noot < SCENE_EERSTE + 8) {
              const slot = noot - SCENE_EERSTE + 1
              const scene = r.scenes.find(sc => sc.midi_slot === slot)
              if (scene) { meld(`APC scene ${slot} → ${scene.name}`); r.onApplyScene?.(scene.id) }
              else meld(`APC scene ${slot} — nog geen scene gekoppeld`)
              return
            }
            if (noot >= TRACK_EERSTE && noot < TRACK_EERSTE + 8) {
              const rij = noot - TRACK_EERSTE
              const sc = r.screens.find(s => (s.midi_rows?.length ? s.midi_rows.includes(rij) : s.midi_row === rij))
              if (sc) {
                const nieuweStand = !sc.rotation_active
                meld(`APC → ${sc.name} (rij ${rij + 1}): auto-switch ${nieuweStand ? 'aan' : 'uit'}`)
                r.onToggleRotation?.(sc, nieuweStand, rij)
              } else {
                meld(`APC track ${rij + 1} — geen scherm op deze rij`)
              }
              return
            }
            voerPadUit(apcIndeling().get(noot), 'APC')
            return
          }
          if (evt.type === 0xB0) {
            const cc = evt.data1
            if (cc < FADER_EERSTE || cc >= FADER_EERSTE + 8) return
            const rij = cc - FADER_EERSTE
            const sc = r.screens.find(s => (s.midi_rows?.length ? s.midi_rows.includes(rij) : s.midi_row === rij))
            if (!sc) return
            const sec = faderNaarSeconden(evt.data2 ?? 0)
            meld(`APC fader ${rij + 1} → ${sc.name}: elke ${sec}s`)
            clearTimeout(faderTimers.current[sc.id])
            faderTimers.current[sc.id] = setTimeout(() => r.onSetInterval?.(sc, sec), 400)
          }
        }))
        nieuw.apc = 'klaar'
      }

      if (apcOut) { apcOutRef.current = apcOut; tekenLeds() }
      setStatus(nieuw)
    })()

    return () => {
      gestopt = true
      losmaken.forEach(f => { try { f() } catch {} })
      Object.values(faderTimers.current).forEach(clearTimeout)
      if (apcOutRef.current) { try { clearAllLeds(apcOutRef.current) } catch {} }
      apcOutRef.current = null
    }
  }, [actief, keuze.apc, keuze.apcOut, apcIndeling, voerPadUit, tekenLeds, meld])

  useEffect(() => { if (status.apc === 'klaar') tekenLeds() },
            [screens, presets, scenes, status.apc, tekenLeds])

  return { actief, zet, status, laatste, inputs, outputs, keuze, kies }
}

/* ── Paneel ──────────────────────────────────────────────────────── */

export default function ScreenMidiPanel({ midi, screens, presets, scenes = [] }) {
  const { actief, zet, status, laatste, inputs, outputs, keuze, kies } = midi
  const [open, setOpen] = useState(false)
  const beideUit = status.apc !== 'klaar'

  return (
    <Card className="mb-4"
          style={actief && !beideUit ? { borderLeft: '2px solid #61E294' } : undefined}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="h-10 w-10 rounded-[4px] border border-border bg-card flex items-center justify-center shrink-0">
            <Sliders className={`h-5 w-5 ${actief ? 'text-acid' : 'text-muted-foreground'}`} />
          </div>
          <div className="flex-1 min-w-[15rem]">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Bediening met MIDI
            </div>
            <div className="flex items-center gap-4 flex-wrap mt-0.5">
              <Apparaat naam="APC Mini mk2" rol="regietafel" status={status.apc} icon={Grid3x3} />
            </div>
            {laatste && <div className="font-mono text-[10px] text-live mt-1.5">{laatste}</div>}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant={actief ? 'default' : 'outline'} onClick={() => zet(!actief)}>
              {actief ? <Zap className="h-4 w-4 mr-1.5" /> : <ZapOff className="h-4 w-4 mr-1.5" />}
              {actief ? 'Aan' : 'Aanzetten'}
            </Button>
            {actief && <Button size="sm" variant="ghost" onClick={() => setOpen(o => !o)}>Apparaten</Button>}
          </div>
        </div>

        {actief && beideUit && (
          <div className="flex items-start gap-2 mt-3 pt-3 border-t border-border/40 text-xs text-signal">
            <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>
              Geen APC gevonden. Web MIDI werkt alleen via https of localhost —
              op http-verbindingen weigert de browser toegang.
            </span>
          </div>
        )}

        {actief && open && (
          <div className="mt-3 pt-3 border-t border-border/40 grid gap-3 sm:grid-cols-2">
            <Veld label="APC — ingang">
              <select value={keuze.apc} onChange={e => kies('apc', e.target.value)} className={selectCls}>
                <option value="">automatisch</option>
                {inputs.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </Veld>
            <Veld label="APC — uitgang (leds)">
              <select value={keuze.apcOut} onChange={e => kies('apcOut', e.target.value)} className={selectCls}>
                <option value="">automatisch</option>
                {outputs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </Veld>
          </div>
        )}

        {actief && <Legenda screens={screens} presets={presets} scenes={scenes} />}
      </CardContent>
    </Card>
  )
}

const selectCls = 'w-full rounded-[4px] border border-border bg-background px-2 py-1.5 text-sm'

function Veld({ label, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
      {children}
    </div>
  )
}

function Apparaat({ naam, rol, status, icon: Icon }) {
  const kleur = { klaar: 'text-live', uit: 'text-muted-foreground' }[status] ?? 'text-peach'
  const tekst = { klaar: 'verbonden', uit: 'uit' }[status] ?? status
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon className={`h-3.5 w-3.5 ${kleur}`} />
      <span className="text-sm font-semibold">{naam}</span>
      <span className={`font-mono text-[10px] uppercase tracking-widest ${kleur}`}>{tekst}</span>
      <span className="font-mono text-[10px] text-muted-foreground">· {rol}</span>
    </span>
  )
}

/* Groot leesbaar raster dat 1-op-1 lijkt op de APC:
   rijen = schermen (in APC-volgorde), kolommen = inhouden. */
function Legenda({ screens, presets, scenes }) {
  // 2D-matrix: rij kan meerdere schermen bevatten. Legenda toont per rij het
  // eerste scherm + telt de rest onder de naam.
  const rijenVan = (x) => (x?.midi_rows?.length
    ? x.midi_rows
    : (x?.midi_row != null ? [x.midi_row] : []))
  const rijen = [0,1,2,3,4,5,6,7].map(rij => screens.filter(s => rijenVan(s).includes(rij)))
  const heeftInhoud = rijen.some(arr => arr.length > 0)

  return (
    <div className="mt-4 pt-4 border-t border-border/40 space-y-5">
      <div>
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
          APC-raster · rij = scherm · kolom = inhoud (per scherm-soort)
        </div>

        {!heeftInhoud && (
          <p className="text-sm text-muted-foreground">
            Nog geen schermen op een APC-rij. Zet in de lijst onderaan de rij van elk scherm.
          </p>
        )}

        {heeftInhoud && (
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full">
              {/* Kolom-headers: nummer 1-8 */}
              <div className="flex gap-1.5 mb-1.5 ml-[10rem]">
                {[0,1,2,3,4,5,6,7].map(k => (
                  <div key={k} className="w-24 shrink-0 text-center font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                    Kol {k + 1}
                  </div>
                ))}
              </div>

              {rijen.map((schermenOpRij, ri) => {
                const s = schermenOpRij[0] || null   // representant voor kleur/kind
                const extra = schermenOpRij.length - 1
                return (
                <div key={ri} className="flex gap-1.5 items-center mb-1.5">
                  <div className="w-[10rem] shrink-0 pr-2 truncate">
                    {s ? (
                      <div className="flex flex-col leading-tight">
                        <span className="text-sm font-semibold truncate" style={{ color: schermKleur(s) }}>
                          {s.name}{extra > 0 && ` +${extra}`}
                        </span>
                        <span className="font-mono text-[10px] uppercase tracking-widest"
                              style={{ color: schermKleur(s) }}
                              title={schermenOpRij.map(x => x.name).join(', ')}>
                          rij {ri + 1} · {s.kind === 'admin' ? 'admin' : 'deelnemer'}
                          {extra > 0 && ` · ${schermenOpRij.length} schermen`}
                        </span>
                      </div>
                    ) : (
                      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/60">
                        rij {ri + 1} — leeg
                      </span>
                    )}
                  </div>
                  {[0,1,2,3,4,5,6,7].map(k => {
                    const preset = s ? presets.find(p => p.kind === (s.kind || 'participant')
                                                       && p.midi_col === k
                                                       && (p.midi_rows?.length ? p.midi_rows.includes(ri) : true)) : null
                    const actief = s && preset && schermenOpRij.every(x => x.current_route === preset.route)
                    const kleur = preset ? presetKleur(preset) : null
                    const Icon = preset ? icoonVan(preset.icon_name) : null
                    return (
                      <div key={k}
                           className="w-24 h-16 shrink-0 rounded-[4px] border flex flex-col items-center justify-center gap-1 relative"
                           style={{
                             borderColor: preset ? (actief ? kleur : `${kleur}66`) : 'var(--border, #333)',
                             background: actief ? `${kleur}22` : preset ? `${kleur}0d` : 'transparent',
                           }}>
                        {preset ? (
                          <>
                            <Icon className="h-4 w-4" style={{ color: kleur }} />
                            <span className="text-[11px] font-semibold text-center leading-tight px-1 truncate max-w-full"
                                  style={{ color: actief ? kleur : undefined }}>
                              {preset.label}
                            </span>
                            {actief && <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full"
                                              style={{ background: kleur }} />}
                          </>
                        ) : (
                          <span className="font-mono text-[9px] text-muted-foreground/40">—</span>
                        )}
                      </div>
                    )
                  })}
                </div>
              )})}
            </div>
          </div>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">
            Scene-knoppen · rechts
          </div>
          {scenes.filter(sc => sc.midi_slot).length === 0 ? (
            <p className="text-sm text-muted-foreground">Nog geen scenes met een knopnummer.</p>
          ) : (
            <div className="space-y-1.5">
              {scenes.filter(sc => sc.midi_slot).sort((a, b) => a.midi_slot - b.midi_slot).map(sc => (
                <div key={sc.id} className="flex items-center gap-2 text-sm">
                  <span className="font-mono w-6 text-gold shrink-0 text-center">{sc.midi_slot}</span>
                  <span className="truncate">{sc.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">
            Track-knoppen · onder — één per APC-rij
          </div>
          <div className="space-y-1.5">
            {[0,1,2,3,4,5,6,7].map(i => {
              const sc = screens.find(s => s.midi_row === i)
              return (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <span className="font-mono w-6 text-muted-foreground shrink-0 text-center">{i + 1}</span>
                  {sc ? (
                    <>
                      <RotateCw className={`h-3 w-3 shrink-0 ${sc.rotation_active ? 'text-live' : 'text-muted-foreground'}`} />
                      <span className="truncate flex-1">{sc.name}</span>
                      <span className="font-mono text-[10px] text-muted-foreground shrink-0">
                        {sc.rotation_active ? `elke ${sc.rotation_seconds ?? 30}s` : 'uit'}
                      </span>
                    </>
                  ) : (
                    <span className="text-muted-foreground/50">geen scherm</span>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <p className="text-xs text-muted-foreground leading-relaxed">
        Faders zetten het rotatie-interval per rij: fader 1 → scherm op rij 1, enz.
        Helemaal onder is 5 seconden, helemaal boven 3 minuten.
      </p>
    </div>
  )
}
